import { useState, useEffect, useMemo } from 'react';
import { applyTheme, type ResolvedTheme } from '@/lib/orgTheme';

/**
 * Debounce by VALUE, not by reference.
 *
 * Callers often build their theme object inline on every render, so the
 * outer reference changes constantly — if we keyed the debounce on identity
 * the timer would never fire. Instead we serialise the value and only re-arm
 * the timer when the content actually changes.
 */
function useDebounce<T>(value: T, ms: number): T {
  const key = useMemo(() => {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }, [value]);
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ms]);
  return debounced;
}

/**
 * Hook that applies a theme preview in real-time as form values change.
 * Debounces by 200ms to avoid excessive DOM updates.
 * Cleans up (reverts) on unmount.
 */
export function useThemePreview(theme: ResolvedTheme | null): void {
  const debounced = useDebounce(theme, 200);

  useEffect(() => {
    if (!debounced) return;

    // Only apply if at least one value is set
    const hasAnyValue = Object.values(debounced).some(v => v !== null && v !== true && v !== false);
    if (hasAnyValue) {
      applyTheme(debounced);
    }

    return () => {
      applyTheme(null);
    };
  }, [debounced]);
}
